import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaPlus, FaSave, FaTrashAlt } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import Highlighter from "react-highlight-words";
import dayjs from "dayjs";

import { Button, ScreenContainer, Text, Input, DateInput, Table } from "components";

import { CONSTANTS_COLORS } from "config/constants/Colors";
import { BUTTON_CONFIG } from "config/constants/Button";
import { CURRENCY_CODES } from "config/constants/Currency";
import {
  actionCotizacionesClean,
  actionCotizacionesDelete,
  actionCotizacionesGetAll,
  actionCotizacionesSave,
  actionCotizacionesUpdate,
  selectAllCotizaciones,
  selectStatusCotizaciones,
} from "pages/dollar_quotation/redux";
import ConfirmationModal from "components/ConfirmationModal";
import CustomModal from "components/CustomModal";
import { actionMessage } from "redux/shared/SharedSlice";

const DATE_FORMAT_DISPLAY = "DD/MM/YYYY";
const DATE_FORMAT_INPUT = "YYYY-MM-DD";

const CotizacionesList = () => {
  const oDispatch = useDispatch();
  const aCotizaciones = useSelector(selectAllCotizaciones);
  const [sFilter, setFilter] = useState("");
  const [aFilteredCotizaciones, setFilteredCotizaciones] = useState([]);
  const bStatus = useSelector(selectStatusCotizaciones);

  const [bDelete, setDelete] = useState(false);
  const [oCotizacionDelete, setCotizacionDelete] = useState(null);

  const [bNew, setNew] = useState(false);
  const [dFecha, setFecha] = useState(null);
  const [sCotizacion, setCotizacion] = useState("");

  const [oCotizacionUpdate, setCotizacionUpdate] = useState(null);

  useEffect(() => {
    oDispatch(actionCotizacionesGetAll());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (bStatus) {
      onClose();
      oDispatch(actionCotizacionesClean());
    }
  }, [bStatus]);

  useEffect(() => {
    setFilteredCotizaciones(() => {
      return aCotizaciones?.length
        ? aCotizaciones
          .filter(oCotizacion => dayjs(oCotizacion.fecha).format(DATE_FORMAT_DISPLAY).includes(sFilter))
          .map(oCotizacion => ({ ...oCotizacion, sFilter }))
        : [];
    });
  }, [aCotizaciones, sFilter]);

  const onDelete = oCotizacion => {
    setCotizacionDelete(oCotizacion);
    setDelete(true);
  };

  const onConfirmDelete = () => {
    setDelete(false);
    setCotizacionDelete(null);
    oDispatch(actionCotizacionesDelete({ nMonId: oCotizacionDelete.monId, sFecha: dayjs(oCotizacionDelete.fecha).format(DATE_FORMAT_INPUT) }));
  };

  const onClose = () => {
    setDelete(false);
    setCotizacionDelete(null);
    setNew(false);
    setFecha(null);
    setCotizacion("");
    setCotizacionUpdate(null);
  };

  const onConfirm = () => {
    if (!dFecha) return oDispatch(actionMessage({ message: "Fecha requerida" }));
    if (!sCotizacion) return oDispatch(actionMessage({ message: "Cotización requerida" }));

    const oData = {
      monId: CURRENCY_CODES.DOLLAR,
      fecha: dayjs(dFecha).format("YYYY-MM-DDT00:00:00"),
      cotizacion: parseFloat(sCotizacion),
    };

    if (oCotizacionUpdate) return oDispatch(actionCotizacionesUpdate(oData));
    oDispatch(actionCotizacionesSave(oData));
  };

  const onUpdate = oCotizacion => {
    setCotizacionUpdate(oCotizacion);
    setFecha(new Date(oCotizacion.fecha));
    setCotizacion(oCotizacion.cotizacion);
    setNew(true);
  };

  return (
    <ScreenContainer>
      <section className="grid grid-cols-2">
        <Text bold size="text-3xl" color="text-gray-800 dark:text-white" className="self-center">
          Cotizaciones del dólar
        </Text>
        <Button
          className="flex align-middle"
          containerClassName="ml-auto"
          label="Nueva"
          onClick={() => setNew(true)}
          type={BUTTON_CONFIG.CLASS.SUCCESS}
          icon={<FaPlus size={20} color={CONSTANTS_COLORS.COLORS.WHITE_COLOR} className="ml-2" />}
        />
      </section>
      <section className="grid grid-cols-4 rounded-lg bg-white dark:bg-gray-800 py-4 px-5 mt-5">
        <div className="relative col-span-4 md:col-span-1">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FiSearch className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </div>
          <Input
            withoutClass
            value={sFilter}
            floatLabel={false}
            onChange={setFilter}
            placeholder="Buscar por fecha (dd/mm/aaaa)"
            containerClassName="rounded-lg overflow-hidden"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          />
        </div>
      </section>
      <section className="py-5">
        <Table data-testid={"cotizacionesTable"}>
          <Table.Header>
            <Table.Row isHeaderOrFooter>
              <Table.Col scope="col" className="px-6 py-3 text-center">
                #
              </Table.Col>
              <Table.Col scope="col" className="px-6 py-3 text-center">
                Fecha
              </Table.Col>
              <Table.Col scope="col" className="px-6 py-3 text-center">
                Cotización
              </Table.Col>
              <Table.Col scope="col" className="py-3 text-center" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {aFilteredCotizaciones.length > 0 ? (
              aFilteredCotizaciones.map((oCotizacion, nIndex) => (
                <Table.Row
                  key={`${oCotizacion.monId}-${oCotizacion.fecha}`}
                  className="cursor-pointer"
                  onClick={() => onUpdate(oCotizacion)}
                >
                  <Table.Col
                    scope="row"
                    className="text-xs md:text-sm px-2 md:px-6 py-4 font-medium text-gray-800 dark:text-white whitespace-nowrap text-center"
                  >
                    {nIndex + 1}
                  </Table.Col>
                  <Table.Col className="px-6 py-4 text-center">
                    <Highlighter
                      searchWords={[oCotizacion.sFilter]}
                      autoEscape={true}
                      textToHighlight={dayjs(oCotizacion.fecha).format(DATE_FORMAT_DISPLAY)}
                    />
                  </Table.Col>
                  <Table.Col className="px-6 py-4 text-center">
                    {oCotizacion.cotizacion}
                  </Table.Col>
                  <Table.Col className="px-2 text-center">
                    <div className="flex justify-center">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(oCotizacion);
                        }}
                        size={BUTTON_CONFIG.SIZE.SM}
                        type={BUTTON_CONFIG.CLASS.DANGER}
                        icon={<FaTrashAlt size={16} color={CONSTANTS_COLORS.COLORS.WHITE_COLOR} />}
                      />
                    </div>
                  </Table.Col>
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Col className="px-6 py-4 text-center" colSpan={4}>
                  <em>
                    {sFilter.length > 0
                      ? "No se encontraron resultados"
                      : "Sin cotizaciones registradas"}
                  </em>
                </Table.Col>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </section>
      <ConfirmationModal
        show={bDelete}
        onClose={() => setDelete(false)}
        onConfirm={onConfirmDelete}
        title="Eliminar cotización"
        question={`Está seguro que desea eliminar la cotización del ${oCotizacionDelete ? dayjs(oCotizacionDelete.fecha).format(DATE_FORMAT_DISPLAY) : ""}`}
        message="Esta acción no se puede revertir"
      />
      <CustomModal show={bNew} title={oCotizacionUpdate ? `Editando cotización del ${dayjs(oCotizacionUpdate.fecha).format(DATE_FORMAT_DISPLAY)}` : "Nueva cotización"} className="w-96" onClose={onClose}>
        <DateInput
          value={dFecha}
          labelClass="mb-1"
          onChange={setFecha}
          disabled={!!oCotizacionUpdate}
          className="w-full"
          label="Fecha"
          placeholder="Fecha"
        />
        <p className="mt-4">Cotización</p>
        <Input
          type="number"
          value={sCotizacion}
          onChange={setCotizacion}
          placeholder="Cotización"
          containerClassName="mt-2"
          className="w-full"
        />
        <div className="mt-5 md:text-right flex justify-center md:justify-end">
          <Button
            label="Cancelar"
            className="mr-4"
            onClick={onClose}
            size={BUTTON_CONFIG.SIZE.SM}
            type={BUTTON_CONFIG.CLASS.OUTLINE}
          />
          <Button
            label="Guardar"
            onClick={onConfirm}
            size={BUTTON_CONFIG.SIZE.SM}
            type={BUTTON_CONFIG.CLASS.SUCCESS}
            icon={<FaSave size={20} color={CONSTANTS_COLORS.COLORS.WHITE_COLOR} className="ml-2" />}
          />
        </div>
      </CustomModal>
    </ScreenContainer>
  );
};

export default CotizacionesList;
